import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, INR, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { FileText, Upload, Clock, CheckCircle2, XCircle, TrendingUp } from "lucide-react";

const statusColor = (s) => ({
  "New": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Contacted": "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  "In Review": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  "Approved": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Disbursed": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Rejected": "bg-red-500/15 text-red-300 border-red-500/30",
}[s] || "bg-white/10 text-white/70 border-white/10");

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("aadhaar");

  const load = async () => {
    const [l, d] = await Promise.all([api.get("/customers/me/leads"), api.get("/documents/mine")]);
    setLeads(l.data); setDocs(d.data);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Max 3MB per file"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/documents", { name: file.name, doc_type: docType, data_url: reader.result });
        toast.success("Document uploaded");
        load();
      } catch (err) { toast.error(formatErr(err)); } finally { setUploading(false); e.target.value = ""; }
    };
    reader.readAsDataURL(file);
  };

  const active = leads.filter(l => !["Rejected","Disbursed"].includes(l.status)).length;
  const approved = leads.filter(l => l.status === "Approved" || l.status === "Disbursed").length;

  return (
    <div data-testid="customer-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="sk-badge sk-badge-gold">Customer portal</div>
          <h1 className="mt-3 font-display text-4xl text-white">Hi, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-white/60 mt-1">Here's the status of your loan applications.</p>
        </div>
        <Link to="/apply" className="sk-btn-gold">+ New application</Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi icon={FileText} label="Total applications" value={leads.length} />
        <Kpi icon={Clock} label="Active" value={active} />
        <Kpi icon={CheckCircle2} label="Approved" value={approved} />
        <Kpi icon={TrendingUp} label="Total requested" value={INR(leads.reduce((s,l)=>s+(l.amount||0),0))} />
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2">
          <h2 className="text-white text-xl font-display mb-4">My applications</h2>
          <div className="sk-card-dark divide-y divide-white/5">
            {leads.length === 0 && <div className="p-10 text-center text-white/50">No applications yet. <Link to="/apply" className="gold">Apply now →</Link></div>}
            {leads.map(l => (
              <div key={l.id} data-testid={`my-lead-${l.id}`} className="p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div>
                  <div className="text-white font-medium">{l.loan_type} Loan · {INR(l.amount)}</div>
                  <div className="text-xs text-white/40 mt-1 font-mono">Ref: {l.id.slice(0,8).toUpperCase()} · {new Date(l.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`sk-badge border ${statusColor(l.status)}`}>{l.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-white text-xl font-display mb-4">Documents</h2>
          <div className="sk-card-dark p-6">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Document type</div>
              <select data-testid="doc-type" value={docType} onChange={(e)=>setDocType(e.target.value)} className="sk-input">
                {["aadhaar","pan","salary_slip","bank_statement","other"].map(t=><option key={t} value={t}>{t.replace(/_/g," ").toUpperCase()}</option>)}
              </select>
            </label>
            <label className={`mt-4 block border border-dashed border-[#D4AF37]/40 rounded-lg p-6 text-center cursor-pointer hover:bg-[#D4AF37]/5 transition-colors ${uploading?"opacity-50":""}`}>
              <Upload size={22} className="mx-auto text-[#D4AF37]"/>
              <div className="mt-2 text-sm text-white/70">{uploading ? "Uploading..." : "Click to upload (max 3MB)"}</div>
              <input data-testid="doc-upload" type="file" hidden onChange={onUpload} disabled={uploading}/>
            </label>
            <div className="mt-5 space-y-2">
              {docs.length === 0 && <div className="text-xs text-white/40 text-center">No documents yet</div>}
              {docs.map(d => (
                <div key={d.id} className="flex items-center gap-3 text-sm bg-white/5 rounded-lg p-3">
                  <FileText size={14} className="text-[#D4AF37]"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{d.name}</div>
                    <div className="text-xs text-white/40">{d.doc_type}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="sk-card-dark p-6">
      <Icon size={18} className="text-[#D4AF37]"/>
      <div className="text-xs uppercase tracking-widest text-white/50 mt-3">{label}</div>
      <div className="text-2xl font-display text-white mt-1">{value}</div>
    </div>
  );
}
